<template>
  <v-dialog :value="value" max-width="760" scrollable @input="close">
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon left>mdi-database-import-outline</v-icon>
        {{ $t('onboarding.config-import.title') }}
        <v-spacer />
        <v-btn icon small @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>

      <v-card-text>
        <!-- Fait : ce qui a été créé, et ce qui reste à refaire à la main. -->
        <template v-if="result">
          <v-alert type="success" dense outlined>{{ $t('onboarding.config-import.done') }}</v-alert>
          <div v-for="group in resultGroups" :key="group.key" class="mb-3">
            <div class="text-subtitle-2">{{ $t(`onboarding.config-import.${group.key}`) }}</div>
            <div v-for="item in group.items" :key="item.id" class="text-body-2">
              <v-icon x-small class="mr-1">mdi-check</v-icon>{{ item.name }}
              <span v-if="item.how" class="text-caption text--secondary">· {{ $t(`onboarding.config-import.how.${item.how}`) }}</span>
            </div>
          </div>
          <v-alert v-for="(warning, index) in result.warnings" :key="index" type="warning" dense text class="mb-2">
            {{ warningText(warning) }}
          </v-alert>
        </template>

        <template v-else>
          <p class="text-body-2 text--secondary">{{ $t('onboarding.config-import.explained') }}</p>
          <organization-select
            v-model="fromOrganizationId"
            :label="$t('onboarding.config-import.source')"
            outlined
            dense
            clearable
            @change="loadCatalogue"
          />
          <v-alert v-if="error" type="error" dense outlined class="mt-3">{{ error }}</v-alert>
          <v-skeleton-loader v-if="loading" type="list-item@4" class="mt-3" />

          <template v-else-if="catalogue">
            <div class="mt-4">
              <div class="text-subtitle-2">{{ $t('onboarding.config-import.policies') }}</div>
              <div v-if="!catalogue.policies.length" class="text-caption text--secondary">{{ $t('onboarding.config-import.none') }}</div>
              <v-checkbox v-for="policy in catalogue.policies" :key="policy.id" v-model="policyIds" :value="policy.id" dense hide-details class="mt-1">
                <template #label>
                  <span>{{ policy.name }}</span>
                  <span class="text-caption text--secondary ml-2">{{ $tc('onboarding.config-import.brings-profiles', policy.profileIds.length) }}</span>
                </template>
              </v-checkbox>
            </div>

            <div class="mt-4">
              <div class="text-subtitle-2">{{ $t('onboarding.config-import.profiles') }}</div>
              <div v-if="!catalogue.profiles.length" class="text-caption text--secondary">{{ $t('onboarding.config-import.none') }}</div>
              <v-checkbox
                v-for="profile in catalogue.profiles"
                :key="profile.id"
                :input-value="profileChecked(profile.id)"
                :disabled="profileBrought(profile.id)"
                dense
                hide-details
                class="mt-1"
                @change="toggleProfile(profile.id, $event)"
              >
                <template #label>
                  <span>{{ profile.name }}</span>
                  <span class="text-caption text--secondary ml-2">
                    {{ kindLabel(profile.kind) }} · {{ subscriptionFate(profile) }}
                    <template v-if="profileBrought(profile.id)"> · {{ $t('onboarding.config-import.brought-by-policy') }}</template>
                  </span>
                </template>
              </v-checkbox>
            </div>

            <div class="mt-4">
              <div class="text-subtitle-2">{{ $t('onboarding.config-import.subscriptions') }}</div>
              <div class="text-caption text--secondary">{{ $t('onboarding.config-import.subscriptions-explained') }}</div>
              <div v-if="!catalogue.subscriptions.length" class="text-caption text--secondary">{{ $t('onboarding.config-import.none') }}</div>
              <v-checkbox v-for="subscription in catalogue.subscriptions" :key="subscription.id" v-model="subscriptionIds" :value="subscription.id" dense hide-details class="mt-1">
                <template #label>
                  <span>{{ subscription.name }}</span>
                  <span v-if="subscription.existing" class="text-caption text--secondary ml-2">{{ $t('onboarding.config-import.already-there') }}</span>
                  <span v-if="!subscription.enabled" class="text-caption text--secondary ml-2">{{ $t('onboarding.config-import.disabled') }}</span>
                </template>
              </v-checkbox>
            </div>
          </template>
        </template>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <template v-if="result">
          <v-btn color="primary" small @click="close">{{ $t('onboarding.config-import.close') }}</v-btn>
        </template>
        <template v-else>
          <v-btn text small @click="close">{{ $t('onboarding.config-import.cancel') }}</v-btn>
          <v-btn color="primary" small :disabled="!selectedCount" :loading="importing" @click="runImport">
            {{ $tc('onboarding.config-import.run', selectedCount) }}
          </v-btn>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ConfigurationImportRepository from '@repository/ConfigurationImportRepository'
import OrganizationSelect from '@blocks/Select/OrganizationSelect.vue'
import CommissioningMixin from './CommissioningMixin'

/** Les réglages qui désignent des objets de la source (ils ne se copient pas), et leur libellé. */
const SETTING_LABELS = {
  'membership.organization': 'organization',
  'membership.member-type': 'member-type',
  'membership.groups': 'groups',
  'organization.default-location': 'location',
}

/**
 * Importer la configuration d'une autre organisation (retours de la démo, R3) : ses politiques de recharge, ses profils
 * d'équipement, ses abonnements. Une politique emporte les profils qu'elle désigne (cochés d'office). Un profil arrive
 * toujours avec un abonnement : celui de sa licence s'il est coché, sinon un abonnement du même modèle déjà là, sinon un
 * abonnement par défaut — le serveur décide, la liste l'annonce. Tout est copié : on modifie ensuite à volonté.
 */
export default {
  name: 'ConfigurationImportDialog',

  components: { OrganizationSelect },

  mixins: [CommissioningMixin],

  props: {
    value: { type: Boolean, default: false },
    organizationId: { type: String, required: true },
  },

  data() {
    return {
      fromOrganizationId: null,
      catalogue: null,
      loading: false,
      importing: false,
      error: null,
      policyIds: [],
      profileIds: [],
      subscriptionIds: [],
      result: null,
    }
  },

  computed: {
    /** Les profils que les politiques cochées emportent. */
    broughtProfileIds() {
      const policies = (this.catalogue?.policies || []).filter(p => this.policyIds.includes(p.id))
      return [...new Set(policies.flatMap(p => p.profileIds))]
    },

    selectedCount() {
      return this.policyIds.length + new Set([...this.profileIds, ...this.broughtProfileIds]).size + this.subscriptionIds.length
    },

    resultGroups() {
      return [
        { key: 'policies', items: this.result.policies },
        { key: 'profiles', items: this.result.profiles },
        { key: 'subscriptions', items: this.result.subscriptions },
      ].filter(group => group.items.length)
    },
  },

  watch: {
    value(open) {
      if (open) this.reset()
    },
  },

  methods: {
    reset() {
      Object.assign(this, { fromOrganizationId: null, catalogue: null, error: null, policyIds: [], profileIds: [], subscriptionIds: [], result: null })
    },

    async loadCatalogue() {
      Object.assign(this, { catalogue: null, error: null, policyIds: [], profileIds: [], subscriptionIds: [] })
      if (!this.fromOrganizationId) return
      this.loading = true
      try {
        this.catalogue = await ConfigurationImportRepository.catalogue(this.organizationId, this.fromOrganizationId)
      } catch (e) {
        this.error = this.messageOf(e)
      } finally {
        this.loading = false
      }
    },

    profileBrought(id) {
      return this.broughtProfileIds.includes(id)
    },

    profileChecked(id) {
      return this.profileBrought(id) || this.profileIds.includes(id)
    },

    toggleProfile(id, checked) {
      this.profileIds = checked ? [...new Set([...this.profileIds, id])] : this.profileIds.filter(p => p !== id)
    },

    /** L'abonnement que le profil trouvera en face, comme le serveur le choisira. */
    subscriptionFate(profile) {
      const subscription = (this.catalogue?.subscriptions || []).find(s => s.id === profile.subscriptionId)
      const name = profile.subscriptionName || '-'
      if (subscription && this.subscriptionIds.includes(subscription.id)) return this.$t('onboarding.config-import.fate.imported', { name })
      if (subscription?.existing) return this.$t('onboarding.config-import.fate.existing', { name })
      return this.$t('onboarding.config-import.fate.created', { name })
    },

    async runImport() {
      this.importing = true
      this.error = null
      try {
        this.result = await ConfigurationImportRepository.run(this.organizationId, {
          fromOrganizationId: this.fromOrganizationId,
          policyIds: this.policyIds,
          profileIds: [...new Set([...this.profileIds, ...this.broughtProfileIds])],
          subscriptionIds: this.subscriptionIds,
        })
        this.$emit('imported', this.result)
      } catch (e) {
        this.error = this.messageOf(e)
      } finally {
        this.importing = false
      }
    },

    warningText(warning) {
      if (warning.type === 'access-not-copied') return this.$t('onboarding.config-import.warning.access', { profile: warning.profile })
      const settings = warning.settings.map(key => this.settingLabel(key))
      return this.$t('onboarding.config-import.warning.settings', { policy: warning.policy, settings: settings.join(', ') })
    },

    /** Le réglage en mots : un objet de la source, sinon son libellé du catalogue, en dernier recours sa clé. */
    settingLabel(key) {
      if (SETTING_LABELS[key]) return this.$t(`onboarding.config-import.setting.${SETTING_LABELS[key]}`)
      const label = `onboarding.configuration.setting.${key}`
      return this.$te(label) ? this.$t(label).toLowerCase() : key
    },

    close() {
      this.$emit('input', false)
    },
  },
}
</script>
